import { useState, useEffect, useCallback } from 'react';
import { LogoMark } from '../../components/LogoMark';
import { useNavigate, Link } from 'react-router-dom';
import { UserPlus, Loader2, Check, X } from 'lucide-react';
import { register, getSocialOAuthRedirectUrl } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';
import { useBackendHealth } from '../../hooks/useBackendHealth';
import { humanizeAuthError } from '../../utils/authErrors';
import { passwordRequirements } from '../../utils/passwordRequirements';
import { PasswordInput } from '../../components/ui/PasswordInput';

type PendingAction = null | 'register' | 'google' | 'github';

export function RegisterPage() {
  const navigate = useNavigate();
  const { refetch } = useAuth();
  const { isBackendReady, retryCount, checkNow } = useBackendHealth();

  const isInIframe = window.self !== window.top;

  // When in an iframe, refetch auth on focus (user may have logged in via popup)
  useEffect(() => {
    if (!isInIframe) return;
    const onFocus = async () => { await refetch(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isInIframe, refetch]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const executeRegister = useCallback(async () => {
    try {
      await register({ email, password, name: name || undefined });
      await refetch();
      // Skip CommitmentQuestion + "check your email" flash entirely.
      // The Phase 1 inbox screen (loaded by /onboarding) handles all
      // post-signup messaging and shows the email preview inline.
      navigate('/onboarding');
    } catch (err: unknown) {
      setError(humanizeAuthError(err, 'Registration failed'));
    } finally {
      setIsLoading(false);
    }
  }, [email, password, name, refetch, navigate]);

  const executeOAuth = useCallback(async (provider: 'google' | 'github') => {
    try {
      const url = await getSocialOAuthRedirectUrl(provider);
      if (isInIframe) {
        window.open(url, '_blank');
        setIsLoading(false);
      } else {
        window.location.href = url;
      }
    } catch (err: unknown) {
      setError(humanizeAuthError(err, `Failed to start ${provider} sign up`));
      setIsLoading(false);
    }
  }, [isInIframe]);

  // When backend becomes ready and there's a pending action, auto-fire it
  useEffect(() => {
    if (!isBackendReady || !pendingAction) return;

    setError(null);
    const action = pendingAction;
    setPendingAction(null);

    if (action === 'register') {
      executeRegister();
    } else {
      executeOAuth(action);
    }
  }, [isBackendReady, pendingAction, executeRegister, executeOAuth]);

  const handleCancel = () => {
    setPendingAction(null);
    setIsLoading(false);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password');
      return;
    }

    const failedRequirements = passwordRequirements.filter((req) => !req.test(password));
    if (failedRequirements.length > 0) {
      setError('Password does not meet requirements');
      return;
    }

    setError(null);
    setIsLoading(true);

    if (isBackendReady) {
      await executeRegister();
    } else {
      setPendingAction('register');
      checkNow();
    }
  };

  const handleOAuthRegister = (provider: 'google' | 'github') => {
    setError(null);
    setIsLoading(true);

    if (isBackendReady) {
      executeOAuth(provider);
    } else {
      setPendingAction(provider);
      checkNow();
    }
  };

  const isWaking = pendingAction !== null;

  const wakingMessage = retryCount === 0
    ? 'Waking up Danny...'
    : retryCount < 3
      ? `Waking up Danny... (hang tight, ${retryCount + 1} checks)`
      : `Still waking up... (${retryCount + 1} checks — cold starts can take up to 30s)`;

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center px-4 pt-safe">
      <div className="w-full max-w-md">
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark rounded-xl shadow-card border border-border-subtle-light dark:border-border-subtle-dark p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <LogoMark className="h-10 w-auto" />
          </div>

          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
              Create your account
            </h1>
            <p className="text-text-secondary-light dark:text-text-secondary-dark">Get started with Danny</p>
          </div>

          {error && !isWaking && (
            <div className="mb-6 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/30 rounded-lg">
              <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Waking up banner */}
          {isWaking && (
            <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 text-primary-700 dark:text-primary-300 text-sm rounded-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>{wakingMessage}</span>
              </div>
              <button
                onClick={handleCancel}
                className="text-xs underline opacity-70 hover:opacity-100"
              >
                Cancel
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1.5"
              >
                Name <span className="text-text-tertiary-light dark:text-text-tertiary-dark">(optional)</span>
              </label>
              <input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-shadow"
                placeholder="Your name"
                autoComplete="name"
                disabled={isWaking}
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1.5"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-shadow"
                placeholder="you@example.com"
                autoComplete="email"
                disabled={isWaking}
              />
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-text-secondary-light dark:text-text-secondary-dark mb-1.5"
              >
                Password
              </label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-primary-light dark:text-text-primary-dark placeholder-text-tertiary-light dark:placeholder-text-tertiary-dark focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 transition-shadow"
                placeholder="••••••••"
                autoComplete="new-password"
                disabled={isWaking}
              />

              {/* Always-visible password requirements panel — surfaces the rules
                  upfront (greyed) so users don't get surprised by a "doesn't
                  meet requirements" error after typing 6 lowercase chars. */}
              <div className="mt-2 space-y-1">
                {passwordRequirements.map((req) => {
                  const passes = password.length > 0 && req.test(password);
                  const idle = password.length === 0;
                  return (
                    <div key={req.label} className="flex items-center gap-2 text-xs">
                      {passes ? (
                        <Check className="w-3 h-3 text-green-600 dark:text-green-400" />
                      ) : (
                        <X className={`w-3 h-3 ${idle ? 'text-text-tertiary-light dark:text-text-tertiary-dark' : 'text-text-secondary-light dark:text-text-secondary-dark'}`} />
                      )}
                      <span className={
                        passes
                          ? 'text-green-600 dark:text-green-400'
                          : idle
                            ? 'text-text-tertiary-light dark:text-text-tertiary-dark'
                            : 'text-text-secondary-light dark:text-text-secondary-dark'
                      }>
                        {req.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary-700 hover:bg-primary-800 text-white font-medium rounded-lg shadow-subtle hover:shadow-hover dark:glow-primary dark:hover:glow-primary-hover transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isWaking && pendingAction === 'register' ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {wakingMessage}
                </>
              ) : isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4" />
                  Create Account
                </>
              )}
            </button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-subtle-light dark:border-border-subtle-dark"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-surface-raised-light dark:bg-surface-raised-dark text-text-tertiary-light dark:text-text-secondary-dark">
                  Or continue with
                </span>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleOAuthRegister('google')}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-base-light dark:hover:bg-surface-hover-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isWaking && pendingAction === 'google' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                Google
              </button>

              <button
                type="button"
                onClick={() => handleOAuthRegister('github')}
                disabled={isLoading}
                className="flex items-center justify-center gap-2 px-4 py-2.5 border border-border-hover-light dark:border-border-hover-dark rounded-lg bg-surface-raised-light dark:bg-surface-base-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-base-light dark:hover:bg-surface-hover-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isWaking && pendingAction === 'github' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd"/>
                  </svg>
                )}
                GitHub
              </button>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
